import * as React from 'react';
import { ISuggestProps } from '../../types';
import { TSetItemActionTypes, useSuggestKeyboard } from './useSuggestKeyboard';

export function useSuggestSelectedItem(
  {
    suggestions,
    open,
    onClose,
    onSelect,
  }: Pick<ISuggestProps<unknown>, 'suggestions' | 'open' | 'onClose' | 'onSelect'>,
  {
    suggestionsChildrenRef,
    listBoxRef,
  }: { suggestionsChildrenRef: React.RefObject<HTMLElement | null>; listBoxRef: React.RefObject<HTMLElement | null> },
) {
  const [selectedItemIndex, setSelectedItemIndex] = React.useState<undefined | number>(undefined);

  /** Без начальной подсветки пункта; выбор с клавиатуры/мышью после взаимодействия. */
  React.useEffect(() => {
    if (open) {
      setSelectedItemIndex(undefined);
    }
  }, [open]);

  const onEscape = React.useCallback(() => open && onClose?.({ source: 'escapeKey' }), [onClose, open]);
  const onEnter = React.useCallback(
    () =>
      selectedItemIndex !== undefined &&
      suggestions[selectedItemIndex] &&
      open &&
      onSelect?.(suggestions[selectedItemIndex], { source: 'keyboard' }),
    [onSelect, suggestions, selectedItemIndex, open],
  );

  const onKeyboardSetItem = React.useCallback(
    (actionType: TSetItemActionTypes) => {
      setSelectedItemIndex(previousIndex => {
        const len = suggestions.length;

        if (len === 0) {
          return undefined;
        }

        const firstSelectable = (): number | undefined => {
          for (let i = 0; i < len; i += 1) {
            if (suggestions[i]?.type !== 'title') {
              return i;
            }
          }

          return undefined;
        };

        const lastSelectable = (): number | undefined => {
          for (let i = len - 1; i >= 0; i -= 1) {
            if (suggestions[i]?.type !== 'title') {
              return i;
            }
          }

          return undefined;
        };

        let nextIndex: number;

        switch (actionType) {
          case 'first': {
            const i = firstSelectable();

            if (i === undefined) {
              return previousIndex;
            }

            nextIndex = i;
            break;
          }

          case 'last': {
            const i = lastSelectable();

            if (i === undefined) {
              return previousIndex;
            }

            nextIndex = i;
            break;
          }

          case 'increment': {
            if (previousIndex === undefined) {
              const i = firstSelectable();

              if (i === undefined) {
                return previousIndex;
              }

              nextIndex = i;
              break;
            }

            let n = previousIndex + 1;

            while (n <= len - 1 && suggestions[n]?.type === 'title') {
              n += 1;
            }

            if (n > len - 1) {
              return previousIndex;
            }

            nextIndex = n;
            break;
          }

          case 'decrement': {
            if (previousIndex === undefined) {
              const i = lastSelectable();

              if (i === undefined) {
                return previousIndex;
              }

              nextIndex = i;
              break;
            }

            let n = previousIndex - 1;

            while (n >= 0 && suggestions[n]?.type === 'title') {
              n -= 1;
            }

            if (n < 0) {
              return previousIndex;
            }

            nextIndex = n;
            break;
          }

          default:
            return previousIndex;
        }

        if (suggestions[nextIndex]?.type === 'title') {
          return previousIndex;
        }

        listBoxRef.current?.children[nextIndex]?.scrollIntoView({ block: 'nearest' });

        return nextIndex;
      });
    },
    [listBoxRef, suggestions],
  );

  useSuggestKeyboard({ onEnter, onEscape, onKeyboardSetItem, suggestionsChildrenRef, listBoxRef });

  return { suggestionsChildrenRef, setSelectedItemIndex, selectedItemIndex };
}
